import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, getDoc, Timestamp } from "firebase/firestore";
import { db } from "./firebase";
import { COLLECTIONS } from "./collections";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { taskToFirestore } from "../models/task";

class TaskService {
    /**
     * Retorna a coleção de tarefas do Tenant
     */
    static getCollectionRef(idTenant) {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório");
        return collection(db, "tenants", idTenant, COLLECTIONS.TASKS || "tasks");
    }

    /**
     * Resgata as tarefas operacionais de um tenant, permitindo filtros simplificados.
     */
    static async getTasks(idTenant) {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório");
        
        try {
            const tasksRef = this.getCollectionRef(idTenant);
            // Poderíamos criar orderBys se tivessem índices. 
            // Como é um kanban operacional geral, consultamos todas ou limitadas por data futura se escalonar muito.
            const querySnapshot = await getDocs(query(tasksRef));
            
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error("Erro ao buscar tarefas:", error);
            throw error;
        }
    }

    /**
     * Resgata as tarefas associadas a um membro específico
     */
    static async getTasksByMember(idTenant, idMember) {
        if (!idTenant || !idMember) throw new Error("Parâmetros obrigatórios ausentes");
        
        try {
            const { where } = await import("firebase/firestore");
            const tasksRef = this.getCollectionRef(idTenant);
            const querySnapshot = await getDocs(query(tasksRef, where("idMember", "==", idMember)));
            
            const docs = [];
            querySnapshot.forEach(doc => {
                docs.push({ id: doc.id, ...doc.data() });
            });
            return docs.sort((a, b) => {
                const aTime = a.createdAt?.seconds || 0;
                const bTime = b.createdAt?.seconds || 0;
                return bTime - aTime;
            });
        } catch (error) {
            console.error("Erro ao buscar tarefas do membro:", error);
            return [];
        }
    }

    /**
     * Cria uma nova Tarefa
     */
    static async createTask(idTenant, authUser, taskData) {
        if (!idTenant || !authUser || !taskData) {
            throw new Error("Parâmetros incompletos para acessar a camada de dados - Tasks");
        }

        try {
            const tenant = { idTenant };
            const preparedData = taskToFirestore(taskData, tenant, authUser);
            
            const tasksRef = this.getCollectionRef(idTenant);
            const newDoc = await addDoc(tasksRef, preparedData);

            await AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.TASKS,
                action: AUDIT_ACTIONS.CREATE,
                entityId: newDoc.id,
                entityName: preparedData.title,
                description: `Tarefa "${preparedData.title}" criada no status ${preparedData.status}.`,
                details: preparedData
            });

            return { id: newDoc.id, ...preparedData };
        } catch (error) {
            console.error("Erro ao criar Tarefa:", error);
            throw error;
        }
    }

    /**
     * Atualiza uma Tarefa
     */
    static async updateTask(idTenant, authUser, taskId, taskData, oldData) {
        if (!idTenant || !taskId || !taskData || !authUser) {
             throw new Error("Parâmetros incompletos para atualização de Tarefa");
        }

        try {
            const tenant = { idTenant };
            
            const preparedData = taskToFirestore(taskData, tenant, authUser);
            // Removendo id pois ele já existe e não se salva id dentro do body nativamente ou atrapalha merge.
            delete preparedData.idTenant; // já garantido na docRef

            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.TASKS || "tasks", taskId);
            
            await updateDoc(docRef, preparedData);

            if (oldData) {
               const changes = AuditService.calculateDiff(oldData, preparedData);
               if (changes) {
                   await AuditService.log({
                        idTenant,
                        user: authUser,
                        module: AUDIT_MODULES.TASKS,
                        action: AUDIT_ACTIONS.UPDATE,
                        entityId: taskId,
                        entityName: preparedData.title,
                        description: `Tarefa "${preparedData.title}" atualizada.`,
                        details: changes
                    });
               }
            }

            return preparedData;
        } catch (error) {
            console.error("Erro ao atualizar Tarefa:", error);
            throw error;
        }
    }

    /**
     * Atualiza especificamente a data de agendamento de uma Tarefa (Evita wipe data)
     */
    static async updateTaskDate(idTenant, authUser, taskId, newDate, oldData) {
        if (!idTenant || !taskId || !newDate || !authUser) {
             throw new Error("Parâmetros incompletos para atualização de data da Tarefa");
        }

        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.TASKS || "tasks", taskId);
            
            const payload = {
                scheduledDate: newDate,
                updatedAt: Timestamp.now(),
                updatedBy: { uid: authUser.uid, name: authUser.displayName || authUser.email || "Sistema" }
            };

            await updateDoc(docRef, payload);

            if (oldData) {
               await AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.TASKS,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: taskId,
                    entityName: oldData.title || 'Tarefa',
                    description: `Data da tarefa atualizada para ${newDate}.`,
                    details: { scheduledDate: { from: oldData.scheduledDate, to: newDate } }
                });
            }

            return true;
        } catch (error) {
            console.error("Erro ao atualizar data da Tarefa:", error);
            throw error;
        }
    }

    /**
     * Update Task Status Only (Drag and Drop Otimizado)
     */
    static async updateTaskStatus(idTenant, authUser, taskId, newStatus) {
        if (!idTenant || !taskId || !newStatus || !authUser) {
            throw new Error("Parâmetros incompletos para atualização de Status da Tarefa");
        }

        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.TASKS || "tasks", taskId);
            const docSnap = await getDoc(docRef);
            
            if(!docSnap.exists()){
                throw new Error("A Tarefa não existe mais.");
            }

            const oldData = docSnap.data();

            // Se o status não mudou, não grava nada (evita duplicatas no histórico)
            if (oldData.status === newStatus) return true;

            const now = Timestamp.now();
            const existingHistory = oldData.statusHistory || [];

            // Deduplicação: se a última entrada já é a mesma transição, não adiciona
            const lastEntry = existingHistory[existingHistory.length - 1];
            const isDuplicate = lastEntry && lastEntry.from === oldData.status && lastEntry.to === newStatus;

            const newHistoryItem = {
                from: oldData.status,
                to: newStatus,
                date: now,
                user: { uid: authUser.uid, name: authUser.displayName || authUser.email || "Sistema" }
            };

            const updatedHistory = isDuplicate ? existingHistory : [...existingHistory, newHistoryItem];

            await updateDoc(docRef, {
                status: newStatus,
                statusHistory: updatedHistory,
                updatedAt: now,
                updatedBy: { uid: authUser.uid, name: authUser.displayName || authUser.email || "Sistema" }
            });

            await AuditService.log({
                 idTenant,
                 user: authUser,
                 module: AUDIT_MODULES.TASKS,
                 action: AUDIT_ACTIONS.UPDATE,
                 entityId: taskId,
                 entityName: oldData.title,
                 description: `Status da tarefa "${oldData.title}" alterado para ${newStatus}.`,
                 details: { status: { from: oldData.status, to: newStatus } }
             });

            return true;
        } catch (error) {
           console.error("Erro ao atualizar status da tarefa:", error);
           throw error; 
        }
    }

    /**
     * Remove uma Tarefa
     */
    static async deleteTask(idTenant, authUser, taskId, taskName) {
        if (!idTenant || !taskId || !authUser) {
             throw new Error("Parâmetros incompletos para excluir uma Tarefa");
        }

        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.TASKS || "tasks", taskId);
            await deleteDoc(docRef);

            await AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.TASKS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: taskId,
                entityName: taskName || 'Tarefa Desconhecida',
                description: `Tarefa "${taskName || taskId}" excluída.`
            });

            return true;
        } catch (error) {
            console.error("Erro ao excluir Tarefa:", error);
            throw error;
        }
    }

    /**
     * Adiciona um comentário a uma tarefa
     */
    static async addComment(idTenant, authUser, taskId, text) {
        if (!idTenant || !taskId || !authUser || !text) {
             throw new Error("Parâmetros incompletos para adicionar comentário");
        }

        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.TASKS || "tasks", taskId);
            const docSnap = await getDoc(docRef);
            
            if(!docSnap.exists()){
                throw new Error("A Tarefa não existe mais.");
            }

            const oldData = docSnap.data();
            const now = window.firebaseTimestamp ? window.firebaseTimestamp() : Timestamp.now(); // Fallback
            
            const newComment = {
                id: Date.now().toString(),
                text: text.trim(),
                createdAt: now,
                createdBy: { uid: authUser.uid, name: authUser.displayName || authUser.email || "Sistema" }
            };

            const updatedComments = [...(oldData.comments || []), newComment];

            await updateDoc(docRef, {
                comments: updatedComments,
                updatedAt: now,
                updatedBy: { uid: authUser.uid, name: authUser.displayName || authUser.email || "Sistema" }
            });

            return newComment;
        } catch (error) {
            console.error("Erro ao adicionar comentário:", error);
            throw error;
        }
    }

    /**
     * Registra o resultado de um contato/ação CRM.
     * 
     * Lógica de negócio:
     * - NO_ANSWER: incrementa contactAttempts, reagenda +1 dia (máx 3 tentativas)
     * - ASKED_DEADLINE / CALLBACK_LATER: reagenda na outcomeDate informada
     * - ENROLLED / RENEWED: move para DONE
     * - GAVE_UP: move para DONE + registra lossReason
     * - SCHEDULED_VISIT / REQUESTED_DISCOUNT: mantém no board, reagenda
     * 
     * @returns {{ action: string, newTaskId?: string }} Ação tomada
     */
    static async registerOutcome(idTenant, authUser, taskId, outcomeData) {
        if (!idTenant || !taskId || !authUser || !outcomeData?.outcome) {
            throw new Error("Parâmetros incompletos para registrar resultado.");
        }

        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.TASKS || "tasks", taskId);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                throw new Error("A Tarefa não existe mais.");
            }

            const oldData = docSnap.data();
            const now = Timestamp.now();
            const userName = authUser.displayName || authUser.email || "Sistema";

            const outcome = outcomeData.outcome;
            const notes = outcomeData.notes?.trim() || "";
            const followUpDate = outcomeData.followUpDate || "";
            const lossReason = outcomeData.lossReason || null;

            // Criar entrada no histórico de resultados
            const outcomeEntry = {
                outcome,
                notes,
                followUpDate,
                lossReason,
                date: now,
                user: { uid: authUser.uid, name: userName }
            };

            const existingOutcomeHistory = oldData.outcomeHistory || [];
            const updatedOutcomeHistory = [...existingOutcomeHistory, outcomeEntry];

            const currentAttempts = oldData.contactAttempts || 0;

            // ═══ Determinar Ação com Base no Resultado ═══
            let updatePayload = {
                contactOutcome: outcome,
                outcomeNotes: notes,
                outcomeDate: followUpDate || null,
                outcomeHistory: updatedOutcomeHistory,
                lastContactAt: now,
                updatedAt: now,
                updatedBy: { uid: authUser.uid, name: userName }
            };

            let actionTaken = "updated";

            // Caso 1: Não Atendeu → Reagenda +1 dia (máx 3 tentativas)
            if (outcome === "no_answer") {
                const newAttempts = currentAttempts + 1;
                updatePayload.contactAttempts = newAttempts;

                if (newAttempts < 3) {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    const tomorrowStr = tomorrow.toISOString().split("T")[0];
                    
                    updatePayload.scheduledDate = tomorrowStr;
                    updatePayload.status = "new";
                    actionTaken = "rescheduled";
                } else {
                    // 3 tentativas esgotadas → vira "frio", fica em overdue para avaliação
                    updatePayload.status = "overdue";
                    actionTaken = "max_attempts";
                }
            }

            // Caso 2: Pediu Prazo / Retornar Depois / Agendou / Pediu Desconto → Aguardando Retorno
            if (["asked_deadline", "callback_later", "scheduled_visit", "requested_discount"].includes(outcome)) {
                updatePayload.contactAttempts = currentAttempts + 1;
                updatePayload.status = "waiting";
                updatePayload.scheduledDate = followUpDate || null;
                actionTaken = "waiting";
            }

            // Caso 3: Matriculou / Renovou / Resgatou / Pagou / Resolvido → Sucesso! Move para DONE
            if (outcome === "enrolled" || outcome === "renewed" || outcome === "retained" || outcome === "paid" || outcome === "resolved") {
                updatePayload.contactAttempts = currentAttempts + 1;
                updatePayload.status = "done";
                actionTaken = "converted";
            }

            // Caso 4: Desistiu → Move para DONE + registra motivo de perda
            if (outcome === "gave_up") {
                updatePayload.contactAttempts = currentAttempts + 1;
                updatePayload.status = "done";
                updatePayload.lossReason = lossReason;
                actionTaken = "lost";
            }

            // Caso 4b: Sem Resposta / Inatividade → Move para DONE (neutro, sem perda obrigatória)
            if (outcome === "no_response_archived") {
                updatePayload.contactAttempts = currentAttempts + 1;
                updatePayload.status = "done";
                actionTaken = "archived_no_response";
            }

            // Caso 5: Trancou Matrícula → Move para DONE (neutro, sem perda)
            if (outcome === "frozen") {
                updatePayload.contactAttempts = currentAttempts + 1;
                updatePayload.status = "done";
                actionTaken = "converted";
            }

            // Caso 6: Agendou Aula Experimental → WAITING até a data da aula
            if (outcome === "trial_scheduled") {
                updatePayload.contactAttempts = currentAttempts + 1;
                updatePayload.status = "waiting";
                updatePayload.scheduledDate = followUpDate || null;
                updatePayload.scheduledTime = "08:00";
                actionTaken = "trial_scheduled";
            }

            // Caso 7: Confirmou Presença → WAITING, mantém data da aula agendada
            if (outcome === "trial_confirmed") {
                updatePayload.contactAttempts = currentAttempts + 1;
                updatePayload.status = "waiting";
                // Mantém a data existente se não vier nova data
                if (followUpDate) updatePayload.scheduledDate = followUpDate;
                actionTaken = "trial_confirmed";
            }

            // Adiciona entrada no statusHistory se o status mudou
            if (updatePayload.status && updatePayload.status !== oldData.status) {
                const existingHistory = oldData.statusHistory || [];
                const newHistoryItem = {
                    from: oldData.status,
                    to: updatePayload.status,
                    date: now,
                    user: { uid: authUser.uid, name: userName }
                };
                updatePayload.statusHistory = [...existingHistory, newHistoryItem];
            }

            await updateDoc(docRef, updatePayload);

            // Auditoria
            await AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.TASKS,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: taskId,
                entityName: oldData.title,
                description: `Resultado CRM registrado: "${outcome}" na tarefa "${oldData.title}"${notes ? ` — ${notes}` : ""}`,
                details: { outcome, notes, followUpDate, lossReason, contactAttempts: updatePayload.contactAttempts }
            });

            return { action: actionTaken };
        } catch (error) {
            console.error("Erro ao registrar resultado CRM:", error);
            throw error;
        }
    }
}

export default TaskService;
